import React, { useEffect, useState } from "react";
import { Col, Descriptions, Modal, Row, Skeleton, Tag, Typography } from "antd";
import { FormatDate } from "components";
import { getBalanceLot } from "services/breakage-service";
import { formatAmountMinor } from "views/breakage/breakage-money";
import {
  agingColor,
  eligibilityColor,
  labelAging,
  labelConsumption,
  labelEligibility,
  labelIneligibilityReason,
  labelOriginType,
  labelOwnerResolution,
} from "views/breakage/breakage-labels";
import { participantLegalName } from "views/breakage/breakage-participant-display";

const { Text } = Typography;

const LotDetailBody = ({ lot }) => {
  const legalName = participantLegalName(lot);
  const documentNumber = lot.participant?.documentNumber;

  return (
    <>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col>
          <Tag color={eligibilityColor[lot.eligibility] || "default"}>
            {labelEligibility(lot.eligibility)}
          </Tag>
        </Col>
        <Col>
          <Tag color={agingColor[lot.agingCondition] || "default"}>
            {labelAging(lot.agingCondition)}
          </Tag>
        </Col>
        {lot.breakageIneligibilityReason && (
          <Col>
            <Tag color="default">
              {labelIneligibilityReason(lot.breakageIneligibilityReason)}
            </Tag>
          </Col>
        )}
      </Row>

      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label="Identificador (suporte)" span={2}>
          <Text copyable={{ text: lot.balanceLotId }}>{lot.balanceLotId}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Nome">
          {(legalName && String(legalName).trim()) || "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Documento">
          {(documentNumber && String(documentNumber).trim()) || "—"}
        </Descriptions.Item>
        <Descriptions.Item label="ID do participante">
          {lot.participantId}
        </Descriptions.Item>
        <Descriptions.Item label="Conta do participante">
          {lot.accountId}
        </Descriptions.Item>
        <Descriptions.Item label="Origem do saldo">
          {labelOriginType(lot.originType)}
        </Descriptions.Item>
        <Descriptions.Item label="Referência da movimentação">
          {lot.sourceTransactionId}
        </Descriptions.Item>
        <Descriptions.Item label="Creditado em">
          <FormatDate value={lot.creditedAt} format="DD/MM/YYYY HH:mm" />
        </Descriptions.Item>
        <Descriptions.Item label="Vence em">
          <FormatDate value={lot.expiresAt} format="DD/MM/YYYY HH:mm" />
        </Descriptions.Item>
        <Descriptions.Item label="Valor original">
          {formatAmountMinor(lot.originalAmount)}
        </Descriptions.Item>
        <Descriptions.Item label="Valor já utilizado">
          {formatAmountMinor(lot.consumedAmount)}
        </Descriptions.Item>
        <Descriptions.Item label="Saldo disponível">
          {formatAmountMinor(lot.remainingAmount)}
        </Descriptions.Item>
        <Descriptions.Item label="Uso pelo participante">
          {labelConsumption(lot.consumptionCondition)}
        </Descriptions.Item>
        <Descriptions.Item label="Empresa responsável" span={2}>
          {labelOwnerResolution(lot.ownerResolutionStatus)}
          {lot.ownerCompanyId ? ` · ${lot.ownerCompanyId}` : ""}
        </Descriptions.Item>
      </Descriptions>

      {Array.isArray(lot.ownerResolutionEvidence) &&
        lot.ownerResolutionEvidence.length > 0 && (
          <>
            <Typography.Title level={5} style={{ marginTop: 24 }}>
              Histórico de confirmação da empresa
            </Typography.Title>
            <Descriptions bordered size="small" column={1}>
              {lot.ownerResolutionEvidence.map((ev, index) => (
                <Descriptions.Item
                  key={ev.bootstrapRunId || index}
                  label={ev.bootstrapRunId || `Registro ${index + 1}`}
                >
                  {labelOwnerResolution(ev.status)} ·{" "}
                  <FormatDate
                    value={ev.resolvedAt}
                    format="DD/MM/YYYY HH:mm"
                  />
                  {ev.ownerCompanyId ? ` · ${ev.ownerCompanyId}` : ""}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </>
        )}
    </>
  );
};

const BreakageLotDetailModal = ({ open, balanceLotId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [lot, setLot] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!open || !balanceLotId) {
        setLot(null);
        return;
      }
      setLoading(true);
      try {
        const result = await getBalanceLot(balanceLotId);
        if (!cancelled) {
          setLot(result?.data || null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, balanceLotId]);

  return (
    <Modal
      title="Detalhes do saldo"
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      destroyOnClose
    >
      {loading && <Skeleton active paragraph={{ rows: 8 }} />}
      {!loading && !lot && (
        <Text type="secondary">
          Não foi possível carregar os detalhes deste saldo.
        </Text>
      )}
      {!loading && lot && <LotDetailBody lot={lot} />}
    </Modal>
  );
};

export default BreakageLotDetailModal;
